import React from 'react'
import type { Appointment, OpeningWindow, TimeBlock, TimeOff } from '../../api/client'
import { useApp } from '../../context/AppContext'
import { formatLongDay } from '../../calendar/dates'
import { blockDefaultEnd } from '../../calendar/dayRows'
import { tapMinute, weekGrid } from '../../calendar/weekGrid'
import { formatPrice } from '../../catalog/format'
import { useNow } from '../../calendar/useNow'
import { Icon } from '../../ui/Icon'

interface WeekViewProps {
  date: string
  today: string
  appointments: Appointment[]
  windows: OpeningWindow[]
  timeOff: TimeOff[]
  timeBlocks: TimeBlock[]
}

// WeekView: each day a column with its appointments as labelled blocks
// (time, the client's whole name and the treatment from 768px, initials on a
// phone; all of it for screen readers), time blocks as neutral buttons under
// them, open bookings in their category's
// colour and completed ones grey, breaks and hours outside work shaded, a
// label on every hour (review 2026-10-14) and a line at the current time.
export default function WeekView({ date, today, appointments, windows, timeOff, timeBlocks }: WeekViewProps) {
  const { dispatch } = useApp()
  const grid = weekGrid(date, windows, timeOff, appointments, today, timeBlocks)
  const span = grid.endMinute - grid.startMinute
  const now = useNow()
  const nowShown = now.date === today && now.minute >= grid.startMinute && now.minute <= grid.endMinute

  const openDay = (day: string) => {
    dispatch({ type: 'SET_CALENDAR_DATE', payload: day })
    dispatch({ type: 'SET_CALENDAR_VIEW', payload: 'day' })
  }

  return (
    <div className="week-view">
      <p className="calendar-summary">
        {grid.count} {grid.count === 1 ? 'appointment' : 'appointments'} · {formatPrice(grid.bookedPence)} booked
      </p>
      <div className="week-grid">
        <div className="week-hours" aria-hidden="true">
          {grid.hours.map((h) => (
            <span key={h} style={{ top: `${((h * 60 - grid.startMinute) / span) * 100}%` }}>
              {String(h).padStart(2, '0')}
            </span>
          ))}
        </div>
        {grid.columns.map((col) => (
          <div key={col.date} className={`week-col${col.isToday ? ' week-col--today' : ''}`}>
            <button
              type="button"
              className="week-col__head"
              aria-label={`${formatLongDay(col.date)}, ${col.count} ${col.count === 1 ? 'appointment' : 'appointments'}${col.timeOff ? ', time off' : col.closed ? ', closed' : ''}`}
              aria-current={col.isToday ? 'date' : undefined}
              onClick={() => openDay(col.date)}
            >
              {/* The whole name from 768px; "Mon" in a phone's narrow column. */}
              <span className="week-col__day" aria-hidden="true">
                <span className="week-col__day-short">{col.weekday.slice(0, 3)}</span>
                <span className="week-col__day-long">{col.weekday}</span>
              </span>
              <span className="week-col__date" aria-hidden="true">{col.dayOfMonth}</span>
            </button>
            <div
              className={`week-col__body${col.closed || col.timeOff ? ' week-col__body--off' : ''}`}
              onClick={(e) => {
                // Hour lines and shaded stretches ignore pointers, so a tap on
                // them lands here; a block or booking is its own button.
                if (e.target !== e.currentTarget) return
                const rect = e.currentTarget.getBoundingClientRect()
                const minute = tapMinute(col, grid, (e.clientY - rect.top) / rect.height)
                if (minute === null) {
                  openDay(col.date)
                } else {
                  dispatch({
                    type: 'OPEN_TAP_CHOICE',
                    payload: { date: col.date, startMinute: minute, endMinute: blockDefaultEnd(col.date, minute, windows, appointments, timeBlocks) },
                  })
                }
              }}
            >
              {grid.hours.map((h) => (
                <span key={h} className="week-hour-line" aria-hidden="true" style={{ top: `${((h * 60 - grid.startMinute) / span) * 100}%` }} />
              ))}
              {col.off.map((o) => (
                <span
                  key={`${o.kind}-${o.top}`}
                  className={`week-off week-off--${o.kind}`}
                  aria-hidden="true"
                  style={{ top: `${o.top * 100}%`, height: `${o.height * 100}%` }}
                />
              ))}
              {col.timeBlocks.map((t) => (
                <button
                  key={t.block.id}
                  type="button"
                  className="week-timeblock"
                  aria-label={t.label}
                  style={{ top: `${t.top * 100}%`, height: `calc(${t.height * 100}% - 2px)` }}
                  onClick={() => dispatch({ type: 'OPEN_BLOCK_SHEET', payload: { date: col.date, startMinute: t.block.start_minute, endMinute: t.block.end_minute, block: t.block } })}
                >
                  <span className="week-timeblock__text" aria-hidden="true">{t.text}</span>
                </button>
              ))}
              {col.blocks.map((b) => (
                <React.Fragment key={b.id}>
                  <button
                    type="button"
                    className={['week-block', b.held && 'week-block--held', b.done && 'week-block--done', b.noShow && 'week-block--no-show', b.short && 'week-block--short']
                      .filter(Boolean)
                      .join(' ')}
                    aria-label={b.label}
                    style={{ top: `${b.top * 100}%`, height: `calc(${b.height * 100}% - 2px)`, '--cat': b.colour } as React.CSSProperties}
                    onClick={() => dispatch({ type: 'OPEN_APPOINTMENT', payload: b.id })}
                  >
                    <span className="week-block__initials" aria-hidden="true">{b.initials}</span>
                    <span className="week-block__text" aria-hidden="true">
                      <span className="week-block__time">
                        {b.done && <Icon name="check" size={12} />}
                        {b.short ? b.start : b.time}
                      </span>{' '}
                      <span className="week-block__name">{b.name}</span>{' '}
                      <span className="week-block__treatment">{b.treatment}</span>
                    </span>
                  </button>
                  {b.blockedHeight > 0 && (
                    <span
                      className="week-block-tail"
                      aria-hidden="true"
                      style={{ top: `calc(${(b.top + b.height) * 100}% - 1px)`, height: `${b.blockedHeight * 100}%`, '--cat': b.colour } as React.CSSProperties}
                    />
                  )}
                </React.Fragment>
              ))}
              {col.isToday && nowShown && (
                <span className="week-now" aria-hidden="true" style={{ top: `${((now.minute - grid.startMinute) / span) * 100}%` }} />
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
